/**
 * components/fasteners/threadedShankGeometry.ts — Geometria procedurale del
 * gambo filettato (28-09-2026, richiesta utente: "il modello delle viti...
 * possiamo migliorare il modello disegnato in modo che somigli a
 * [riferimento fotorealistico]" — poi precisato: "non voglio caricare gli
 * asset veri, modellare simile alle immagini", quindi SOLO geometria
 * procedurale, nessun GLB).
 *
 * APPROSSIMAZIONE DELIBERATA ("migliorato, non perfetto" — richiesta
 * utente): il profilo alterna raggio radice/cresta lungo Y e viene
 * rivoluzionato di 360° in un colpo solo (`THREE.LatheGeometry`) — il
 * risultato sono ANELLI concentrici, non una VERA elica continua. Una elica
 * vera richiederebbe un profilo 2D (il dente del filetto) spazzato lungo una
 * curva elicoidale 3D (es. `THREE.ExtrudeGeometry` su una `THREE.Curve`
 * custom) — molto piu' costoso da calcolare e mantenere leggero con decine
 * di viti in scena. Gli anelli leggono comunque come "filettatura" alla
 * distanza/zoom normali del viewer, che e' l'obiettivo dichiarato.
 *
 * PUNTA (corretta 28-09-2026, bug segnalato dall'utente — "la punta è
 * strana"): un cono liscio attaccato di netto al gambo filettato crea una
 * discontinuita' visibile (il filetto "sparisce" di colpo). Il filetto
 * CONTINUA quindi anche nella punta, rimpicciolendo progressivamente
 * l'inviluppo radice/cresta fino a zero — stessa idea di una vite
 * autofilettante reale, dove la filettatura prosegue fin quasi alla punta.*/

import * as THREE from "three";

export interface ThreadedShankParams {
  /** Raggio nominale del gambo (mm) — la "cresta" del filetto.*/
  radiusMm: number;
  /** Lunghezza totale del gambo, punta inclusa (mm).*/
  lengthMm: number;
  /** Passo del filetto (mm, distanza fra due creste).*/
  pitchMm: number;
  /** Profondita' del filetto, frazione del raggio (0..1).*/
  threadDepthFactor: number;
  /** Lunghezza della zona di punta, dove il filetto rimpicciolisce fino a zero (mm).*/
  tipLengthMm: number;
  /** Segmenti radiali della rivoluzione (qualita' visiva, non filettatura).*/
  segments: number;
}

/**
 * Costruisce il profilo (radice/cresta alternati sul corpo, poi lo stesso
 * pattern con inviluppo decrescente sulla punta) e lo rivoluziona in una
 * `THREE.LatheGeometry`, locale con Y=0 all'attacco sotto la testa e
 * Y=lengthMm alla punta (NON centrata — il chiamante posiziona il mesh di
 * conseguenza, a differenza di `CylinderGeometry` che e' centrata).*/
export function buildThreadedShankGeometry({
  radiusMm,
  lengthMm,
  pitchMm,
  threadDepthFactor,
  tipLengthMm,
  segments,
}: ThreadedShankParams): THREE.LatheGeometry {
  const rootRadius = radiusMm * (1 - threadDepthFactor);
  // La punta non puo' essere piu' lunga del gambo stesso (viti molto corte/tozze).
  const clampedTip = Math.min(tipLengthMm, lengthMm * 0.5);
  const bodyLength = Math.max(lengthMm - clampedTip, pitchMm);
  const threadCount = Math.max(2, Math.round(bodyLength / pitchMm));
  const actualPitch = bodyLength / threadCount;

  const points: THREE.Vector2[] = [new THREE.Vector2(radiusMm, 0)];
  for (let i = 0; i < threadCount; i++) {
    const yBase = i * actualPitch;
    points.push(new THREE.Vector2(rootRadius, yBase + actualPitch * 0.25));
    points.push(new THREE.Vector2(radiusMm, yBase + actualPitch * 0.75));
  }

  // Punta: STESSO pattern radice/cresta del corpo, ma con inviluppo (cresta
  // massima consentita in quel punto) che scende linearmente da radiusMm a 0
  // — il filetto "si esaurisce" nella punta invece di sparire di colpo.
  //
  // BUG (28-09-2026, segnalato dall'utente — viti disallineate/spezzate in
  // scena, causa reale: "LatheGeometry Computed radius is NaN" in console):
  // con un gambo cosi' corto da non lasciare spazio alla punta (clampedTip
  // ~ 0, es. anchor.lengthMm invalido/mancante), `(y - bodyLength) /
  // clampedTip` divide per zero → NaN si propaga a tutta la geometria e
  // manda in crash il contesto WebGL (non un problema di orientamento).
  // Sotto questa soglia si salta la rastremazione e si chiude il gambo
  // di netto: degradazione contenuta di un caso di dati non validi, non
  // piu' un crash dell'intera scena.
  const MIN_TIP_LENGTH_MM = 1e-3;
  if (clampedTip > MIN_TIP_LENGTH_MM) {
    const tipThreadCount = Math.max(1, Math.round(clampedTip / pitchMm));
    const tipPitch = clampedTip / tipThreadCount;
    for (let i = 0; i < tipThreadCount; i++) {
      const yBase = bodyLength + i * tipPitch;
      const yRoot = yBase + tipPitch * 0.25;
      const yCrest = yBase + tipPitch * 0.75;
      const envelopeAtRoot = radiusMm * (1 - (yRoot - bodyLength) / clampedTip);
      const envelopeAtCrest = radiusMm * (1 - (yCrest - bodyLength) / clampedTip);
      points.push(new THREE.Vector2(Math.max(envelopeAtRoot * (1 - threadDepthFactor), 0), yRoot));
      points.push(new THREE.Vector2(Math.max(envelopeAtCrest, 0), yCrest));
    }
  }
  points.push(new THREE.Vector2(0, Math.max(lengthMm, bodyLength)));

  return new THREE.LatheGeometry(points, segments);
}
